import {Button} from '@wandb/weave/components/Button';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {toast} from 'react-toastify';

import YAML from '../../../../services/yaml';
import {useDefaultResourceConfigQuery} from '../../../generated/graphql';
import {CodeEditor} from '../CodeEditor';
import {sortTemplateVariablesByConfig} from '../DefaultResourceConfig/defaultResourceConfigUtil';
import ResourceVariableForm from '../DefaultResourceConfig/ResourceVariableForm';
import * as S from './JobDrawerContentInner.styles';
import {
  getDefaultVariableValuesFromQueueVariables,
  substituteVariables,
} from './utils';

export const JobDrawerAdditionalServiceConfigEditor = ({
  additionalServiceID,
  additionalServiceName,
  config,
  entityName,
  resourceType,
  updateAdditionalService,
  displayMode,
}: {
  additionalServiceID: string;
  additionalServiceName: string;
  config: string;
  entityName: string;
  resourceType: string;
  displayMode: string;
  updateAdditionalService: (
    id: string,
    update: {config?: string; isValid?: boolean; displayType?: string}
  ) => void;
}) => {
  const initialized = useRef(false);
  const [initialConfig, setInitialConfig] = useState('{}');

  const {data: defaultResourceConfig, loading: defaultResourceConfigLoading} =
    useDefaultResourceConfigQuery({
      variables: {
        entityName,
        defaultResourceConfigID: additionalServiceID,
      },
    });
  useEffect(() => {
    if (initialized.current || defaultResourceConfigLoading) {
      return;
    }
    const configData =
      defaultResourceConfig?.entity?.defaultResourceConfig?.config
        ?.resource_args[resourceType];
    if (configData != null) {
      updateAdditionalService(additionalServiceID, {
        config: JSON.stringify(configData, null, 2),
      });
      setInitialConfig(JSON.stringify(configData, null, 2));
    }
    initialized.current = true;
  }, [
    defaultResourceConfig,
    defaultResourceConfigLoading,
    resourceType,
    updateAdditionalService,
    additionalServiceID,
  ]);

  const templateVariables = useMemo(
    () =>
      defaultResourceConfig?.entity?.defaultResourceConfig?.templateVariables ??
      [],
    [defaultResourceConfig]
  );
  const sortedTemplateVariables = useMemo(() => {
    return sortTemplateVariablesByConfig(
      JSON.stringify(initialConfig),
      templateVariables
    );
  }, [initialConfig, templateVariables]);
  const [variableValues, setVariableValues] = useState<
    Record<string, number | string>
  >(getDefaultVariableValuesFromQueueVariables(templateVariables));

  const variantYaml = displayMode === 'yaml' ? 'secondary' : 'ghost';
  const variantJson = displayMode === 'json' ? 'secondary' : 'ghost';
  const [showResourceConfig, setShowResourceConfig] = useState(false);
  const onResourceConfigChange = (value: string) => {
    updateAdditionalService(additionalServiceID, {
      config: value,
      isValid: true,
    });
  };

  const updateVariable = useCallback(
    (name: string, value: string | number) => {
      const templateVar = templateVariables.find(tv => tv.name === name);
      if (templateVar == null) {
        toast(
          'Resource config value not saved, please select resource config values'
        );
        return;
      }
      let updateValue = value;
      try {
        const tvSchema = JSON.parse(templateVar.schema);
        if (tvSchema.type === 'number' || tvSchema.type === 'integer') {
          updateValue = Number(value);
        }
      } catch {
        toast(
          `Invalid schema for variable: ${name} please reconfigure queue by visiting the queue tab.`
        );
        return;
      }
      setVariableValues({
        ...variableValues,
        [name]: updateValue,
      });
    },
    [templateVariables, variableValues]
  );

  useEffect(() => {
    const substitutedConfig = substituteVariables(
      JSON.parse(initialConfig),
      variableValues
    );

    const configString =
      displayMode === 'yaml'
        ? YAML.stringify(substitutedConfig, {indent: 2})
        : JSON.stringify(substitutedConfig, null, 2);

    if (configString !== config) {
      updateAdditionalService(additionalServiceID, {
        config: configString,
      });
    }
  }, [
    displayMode,
    variableValues,
    additionalServiceID,
    initialConfig,
    updateAdditionalService,
    config,
  ]);

  return (
    <>
      <div className="mb-4 mt-16 flex items-center">
        <div className="text-[1rem] font-semibold leading-[2rem]">
          {additionalServiceName} Configurations
        </div>
      </div>
      {sortedTemplateVariables.length > 0 &&
        sortedTemplateVariables.map((variable, i) => (
          <ResourceVariableForm
            key={i}
            name={variable.name}
            schema={variable.schema}
            description={variable.description}
            value={variableValues[variable.name]}
            updateVariable={updateVariable}
          />
        ))}
      <div className="mb-4 mt-16 flex items-center">
        <div>Complete {additionalServiceName} configurations</div>
        <S.ToggleConfig>
          {showResourceConfig && (
            <Button
              variant={variantYaml}
              onClick={() =>
                updateAdditionalService(additionalServiceID, {
                  displayType: 'yaml',
                })
              }>
              YAML
            </Button>
          )}
          {showResourceConfig && (
            <Button
              variant={variantJson}
              onClick={() =>
                updateAdditionalService(additionalServiceID, {
                  displayType: 'json',
                })
              }>
              JSON
            </Button>
          )}
          <Button
            data-test="show-resource-config"
            variant="ghost"
            onClick={() => {
              setShowResourceConfig(!showResourceConfig);
            }}>
            {showResourceConfig ? 'Hide' : 'Show'}
          </Button>
        </S.ToggleConfig>
      </div>
      {showResourceConfig && (
        <CodeEditor
          value={config}
          language={displayMode}
          onChange={onResourceConfigChange}
          readOnly={sortedTemplateVariables.length > 0}
        />
      )}
    </>
  );
};
